import { Inject, Injectable, DOCUMENT } from '@angular/core';

import { from, of, race, Subject } from 'rxjs';
import { RecaptchaComponent } from 'ng-recaptcha';
import { CompanyService } from '@app/shared/services/company.service';
import { environment } from '@environments/environment';
import { featureFlags } from '@utils/feature-flags';

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private companyService: CompanyService
  ) {}

  get isEnabled() {
    const isCompany = Boolean(this.companyService.subdomain);
    const isEnabledForCompany = featureFlags.isOn('CompanyFeature.reCaptcha');

    return !isCompany || isEnabledForCompany;
  }

  execute(recaptcha?: RecaptchaComponent) {
    if (recaptcha) {
      recaptcha.execute();
    }

    return this.resolvedOrDismissed(recaptcha);
  }

  resolvedOrDismissed(recaptcha?: RecaptchaComponent) {
    if (recaptcha) {
      return race(from(recaptcha.resolved), this.rejectWhenDismissed());
    } else {
      return of('');
    }
  }

  /**
   * Used for invisible recaptcha.
   *
   * Returns observable which emits when recaptcha window is closed.
   */
  rejectWhenDismissed(pollInterval = 100) {
    if (!this.isEnabled) {
      return of('');
    }

    const subj = new Subject<string>();

    const searchIntervalIframe = setInterval(() => {
      const iframe = this.document.querySelector('iframe[src*="google.com/recaptcha/api2/bframe"]');
      if (iframe && iframe.parentNode && iframe.parentNode.parentNode) {
        clearInterval(searchIntervalIframe);
        const container = iframe.parentNode.parentNode as HTMLElement;
        let wasVisible = false;

        const whenComplete = setInterval(() => {
          if (container.style.visibility === 'visible') {
            wasVisible = true;
            // The ui-elements ref-modal sets pointer-events: none on <html> while open, which breaks the reCAPTCHA challenge.
            // We need to add a class to <html> so we can override that while the challenge is open.
            this.document.documentElement.classList.add('recaptcha-challenge-open');
          } else if (wasVisible) {
            clearInterval(whenComplete);
            this.document.documentElement.classList.remove('recaptcha-challenge-open');

            subj.next(null);
            subj.complete();
          }
        }, pollInterval);
      }
    }, pollInterval);

    return subj.asObservable();
  }

  get libraryUrlV3() {
    return `https://www.google.com/recaptcha/api.js?render=${environment.RECAPTCHA_V3_SITE_KEY}`;
  }

  loadLibraryV3() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.onload = () => {
        const recaptcha = window.grecaptcha;

        recaptcha.ready(() => {
          resolve(recaptcha);
        });
      };
      script.onerror = () => reject();
      script.src = this.libraryUrlV3;

      document.head.appendChild(script);
    });
  }
}
